import { AnimatePresence, LayoutGroup, motion } from "framer-motion";
import React, { PropsWithChildren } from "react";
import styled from "styled-components";
import { forPhoneOnly } from "../theme/media-queries";

type Props = PropsWithChildren<{}>;

const GridItem = styled(motion.div)`
  position: relative;
  width: 100%;
  max-width: 100%;
  aspect-ratio: 16 / 9;
  background-color: #000;
  border-radius: 5px;
  margin: auto;
`;

const GridOuter = styled.div`
  position: fixed;
  inset: 0;
  bottom: var(--bottom-control-bar-height);
`;

const GridElement = styled.div`
  padding: 32px;
  gap: 20px;

  @media ${forPhoneOnly} {
    padding: 0;
    gap: 5px;
  }
  max-width: 1600px;
  height: 100%;
  margin: auto;
  display: grid;

  &[data-count="1"] {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
    grid-template-areas: "p1";
    & > ${GridItem} {
      height: 100%;
      width: auto;
    }
  }

  &[data-count="2"] {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr;
    grid-template-areas: "p1 p2";
    [data-area="p2"] {
      margin-left: 0;
      --overlay-align: flex-end;
    }
    @media (max-aspect-ratio: 4 / 3) {
      grid-template-rows: 1fr 1fr;
      grid-template-areas:
        "p1 p1"
        "p2 p2";
      & > ${GridItem} {
        height: 100%;
        width: auto;
      }
      [data-area="p2"] {
        margin-left: auto;
        --overlay-align: flex-end;
      }
    }
    @media (max-aspect-ratio: 1 / 1) {
      & > ${GridItem} {
        height: 100%;
        width: 100%;
        aspect-ratio: unset;
      }
    }
  }

  &[data-count="3"] {
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: 1fr 1fr;
    grid-template-areas:
      "p1 p1 p2 p2"
      ". p3 p3 .";
    & > ${GridItem} {
      height: 100%;
      width: auto;
    }
    [data-area="p1"] {
      margin-right: 0;
    }
    [data-area="p2"] {
      margin-left: 0;
      --overlay-align: flex-end;
    }

    @media (max-aspect-ratio: 4 / 3) {
      grid-template-rows: 1fr 1fr;
      grid-template-areas:
        "p1 p1 p2 p2"
        "p3 p3 p3 p3";
      & > ${GridItem} {
        height: 100%;
        width: 100%;
        aspect-ratio: unset;
      }
    }
  }

  &[data-count="4"] {
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: 1fr 1fr;
    grid-template-areas:
      "p1 p1 p2 p2"
      "p4 p4 p3 p3";

    & > ${GridItem} {
      width: auto;
      height: 100%;
    }
    [data-area="p1"],
    [data-area="p4"] {
      margin-right: 0;
    }
    [data-area="p2"],
    [data-area="p3"] {
      margin-left: 0;
      --overlay-align: flex-end;
    }

    @media (max-aspect-ratio: 4 / 3) {
      & > ${GridItem} {
        aspect-ratio: unset;
        height: 100%;
        width: 100%;
      }
    }
  }

  &[data-count="5"] {
    grid-template-columns: repeat(12, 1fr);
    grid-template-rows: min-content min-content;
    grid-template-areas:
      "p1 p1 p1 p1 p2 p2 p2 p2 p3 p3 p3 p3"
      "p5 p5 p5 p5 p5 p5 p4 p4 p4 p4 p4 p4";
    [data-area="p1"],
    [data-area="p5"] {
      margin-right: 0;
    }
    [data-area="p3"],
    [data-area="p4"] {
      margin-left: 0;
      --overlay-align: flex-end;
    }
    @media (max-aspect-ratio: 4 / 3) {
      grid-template-columns: repeat(4, 1fr);
      grid-template-rows: 1fr 1fr 1fr;
      grid-template-areas:
        "p1 p1 p2 p2"
        "p5 p5 p3 p3"
        ".  p4 p4 .";
      & > ${GridItem} {
        aspect-ratio: unset;
        height: 100%;
      }
      [data-area="p1"],
      [data-area="p5"] {
        margin-right: 0;
      }
      [data-area="p2"],
      [data-area="p3"] {
        margin-left: 0;
        --overlay-align: flex-end;
      }
    }
  }
  &[data-count="6"] {
    grid-template-columns: repeat(12, 1fr);
    grid-template-rows: 1fr 1fr;
    grid-template-areas:
      "p1 p1 p1 p1 p2 p2 p2 p2 p3 p3 p3 p3"
      "p6 p6 p6 p6 p5 p5 p5 p5 p4 p4 p4 p4";
    @media (max-aspect-ratio: 4 / 3) {
      grid-template-columns: repeat(4, 1fr);
      grid-template-rows: 1fr 1fr 1fr;
      grid-template-areas:
        "p1 p1 p2 p2"
        "p6 p6 p3 p3"
        "p5 p5 p4 p4";
      & > ${GridItem} {
        aspect-ratio: unset;
        height: 100%;
      }
      [data-area="p1"],
      [data-area="p5"],
      [data-area="p6"] {
        margin-right: 0;
      }
      [data-area="p2"],
      [data-area="p3"],
      [data-area="p4"] {
        margin-left: 0;
        --overlay-align: flex-end;
      }
    }
    @media (max-aspect-ratio: 1 / 1) {
      grid-template-rows: 1fr 1fr 1fr;
      & > ${GridItem} {
        aspect-ratio: unset;
        height: 100%;
      }
    }
  }
  &[data-count="7"] {
    grid-template-columns: repeat(12, 1fr);
    grid-template-rows: 3fr 4fr 4fr;
    grid-template-areas:
      "p1 p1 p1 p1 p2 p2 p2 p2 p3 p3 p3 p3"
      "p7 p7 p7 p7 p7 .  .  p4 p4 p4 p4 p4"
      " . p6 p6 p6 p6 p6 p5 p5 p5 p5 p5  .";
    & > ${GridItem} {
      height: 100%;
      width: auto;
    }
    [data-area="p1"],
    [data-area="p6"],
    [data-area="p7"] {
      margin-right: 0;
    }
    [data-area="p3"],
    [data-area="p4"],
    [data-area="p5"] {
      margin-left: 0;
      --overlay-align: flex-end;
    }
    @media (max-aspect-ratio: 4 / 3) {
    }
    @media (max-aspect-ratio: 1 / 1) {
      grid-template-rows: 3fr 4fr 4fr;
      grid-template-areas:
        "p1 p1 p1 p1 p2 p2 p2 p2 p3 p3 p3 p3"
        "p7 p7 p7 p7 p7 p7 p4 p4 p4 p4 p4 p4"
        "p6 p6 p6 p6 p6 p6 p5 p5 p5 p5 p5 p5";
      & > ${GridItem} {
        aspect-ratio: unset;
        height: 100%;
        width: 100%;
      }
    }
    @media ${forPhoneOnly} {
      /* padding-top: 32px;
      padding-bottom: 32px; */
    }
  }
  &[data-count="8"] {
    grid-template-columns: repeat(12, 1fr);
    grid-template-rows: 1fr 1fr 1fr;
    grid-template-areas:
      "p1 p1 p1 p1 p2 p2 p2 p2 p3 p3 p3 p3"
      " . p8 p8 p8 p8 .  .  p4 p4 p4 p4 ."
      "p7 p7 p7 p7 p6 p6 p6 p6 p5 p5 p5 p5";
    & > ${GridItem} {
      height: 100%;
      width: auto;
    }
    [data-area="p1"],
    [data-area="p7"],
    [data-area="p8"] {
      margin-right: 0;
    }
    [data-area="p3"],
    [data-area="p4"],
    [data-area="p5"] {
      margin-left: 0;
      --overlay-align: flex-end;
    }
    @media (max-aspect-ratio: 1 / 1) {
      grid-template-rows: 3fr 4fr 3fr;
      grid-template-areas:
        "p1 p1 p1 p1 p2 p2 p2 p2 p3 p3 p3 p3"
        "p8 p8 p8 p8 p8 .  .  p4 p4 p4 p4 p4"
        "p7 p7 p7 p7 p6 p6 p6 p6 p5 p5 p5 p5";
      & > ${GridItem} {
        aspect-ratio: unset;
        height: 100%;
        width: 100%;
      }
    }
    @media ${forPhoneOnly} {
      padding-top: 32px;
      padding-bottom: 32px;
    }
  }
`;

const Grid: React.FC<Props> = ({ children }) => {
  const count = React.Children.count(children);
  return (
    <GridOuter>
      <GridElement data-count={count}>
        <LayoutGroup>
          <AnimatePresence>
            {React.Children.map(children, (c, idx) => {
              const gridArea = "p" + (idx + 1);
              const style = {
                gridArea
              };
              return (
                <GridItem
                  layout
                  data-area={gridArea}
                  key={idx}
                  style={style}
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.5 }}
                >
                  {c}
                </GridItem>
              );
            })}
          </AnimatePresence>
        </LayoutGroup>
      </GridElement>
    </GridOuter>
  );
};

export default Grid;
